// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import {
  buildMembershipCandidatePool,
  buildMembershipOptionsByQuery,
  hasExactMembershipNameMatch,
  mergeSearchedCandidates,
} from '@/components/playerMembershipDraft';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { usePlayerSearch } from '@/hooks/usePlayerSearch';
import appColors from '@/theme/colors';
import { card } from '../../typography';
import SectionHeading from '../shared/SectionHeading';
import ConfirmDialog from '../shared/ConfirmDialog';
import type { TourPlayerSnapshot, PlayerRow, ExistingPlayerRow } from '../../types';
import { MAX_PLAYERS } from '../../constants';

type Props = {
  playerRows: PlayerRow[];
  availablePlayers: TourPlayerSnapshot[];
  playerRowOpen: string | null;
  saving: boolean;
  onSetPlayerRows: (updater: (prev: PlayerRow[]) => PlayerRow[]) => void;
  onSetPlayerRowOpen: (rowId: string | null) => void;
  onSendTourMemberAdd: (playerId: string, reason: string, draftRowId?: string) => void;
  onSendTourMemberRemove: (playerId: string) => void;
  onSendTemporaryPlayerCreate: (name: string) => string | undefined;
};

export default function TourPlayersEditor({
  playerRows,
  availablePlayers,
  saving,
  onSendTourMemberAdd,
  onSendTourMemberRemove,
  onSendTemporaryPlayerCreate,
}: Props) {
  const { user } = useUser();
  const { t } = useI18n();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ playerId: string; name: string } | null>(null);

  const existingRows = useMemo(
    () => playerRows.filter((r): r is ExistingPlayerRow => r.kind === 'existing'),
    [playerRows]
  );

  const memberIds = useMemo(() => new Set(existingRows.map((r) => r.playerId)), [existingRows]);

  // Anyone outside the caller's friends and own guests has to be searched for; the server applies
  // each user's search-visibility preference.
  const { results: searchResults } = usePlayerSearch(apiBaseUrl, user?.token, searchQuery);
  const searchableCandidates = useMemo(
    () => mergeSearchedCandidates<TourPlayerSnapshot>(availablePlayers, searchResults),
    [availablePlayers, searchResults]
  );

  // Friends and own guests when the box is empty, widening to searched players once a query is
  // typed. Shared with the round-init pickers so both lists agree.
  const candidatePool = useMemo(
    () =>
      buildMembershipCandidatePool(searchableCandidates, searchQuery).filter(
        (p) => !memberIds.has(p.id)
      ),
    [memberIds, searchableCandidates, searchQuery]
  );

  const filteredOptions = useMemo(
    () => buildMembershipOptionsByQuery(candidatePool, searchQuery, 8),
    [candidatePool, searchQuery]
  );

  const showCreateTemporary = useMemo(() => {
    if (!searchQuery.trim()) return false;
    return !hasExactMembershipNameMatch(candidatePool, searchQuery);
  }, [candidatePool, searchQuery]);

  const canAdd = existingRows.length < MAX_PLAYERS;

  const handleSelectPlayer = useCallback(
    (player: TourPlayerSnapshot) => {
      if (saving || !canAdd) return;
      onSendTourMemberAdd(player.id, 'tour.player.add');
      setSearchQuery('');
      setDropdownOpen(false);
    },
    [canAdd, onSendTourMemberAdd, saving]
  );

  const handleCreateTemporary = useCallback(() => {
    const name = searchQuery.trim();
    if (!name || saving || !canAdd) return;
    onSendTemporaryPlayerCreate(name);
    setSearchQuery('');
    setDropdownOpen(false);
  }, [canAdd, onSendTemporaryPlayerCreate, saving, searchQuery]);

  const handleRemovePlayer = useCallback(
    (row: ExistingPlayerRow) => {
      if (row.locked) return;
      setConfirmRemove({ playerId: row.playerId, name: row.name });
    },
    []
  );

  const handleConfirmRemove = useCallback(() => {
    if (confirmRemove) {
      onSendTourMemberRemove(confirmRemove.playerId);
      setConfirmRemove(null);
    }
  }, [confirmRemove, onSendTourMemberRemove]);

  const showDropdown = dropdownOpen && (filteredOptions.length > 0 || showCreateTemporary);

  return (
    <View style={styles.container}>
      <SectionHeading title={t('tourEdit.tourPlayersTitle')} count={existingRows.length} />

      {/* Existing players */}
      <View style={styles.playerList}>
        {existingRows.map((row) => (
          <View key={row.rowId} style={styles.playerRow}>
            <Text style={styles.playerName}>{row.name}</Text>
            {!row.locked && (
              <Pressable
                style={styles.removeButton}
                onPress={() => handleRemovePlayer(row)}
                accessibilityLabel={t('tourEdit.tourPlayerRemoveA11y', { name: row.name })}
              >
                <Text style={styles.removeIcon}>×</Text>
              </Pressable>
            )}
          </View>
        ))}
      </View>

      {/* Search + dropdown as a connected unit */}
      {canAdd && (
        <View style={styles.searchWrapper}>
          <TextInput
            style={[styles.searchInput, showDropdown && styles.searchInputOpen]}
            value={searchQuery}
            onChangeText={(value) => {
              setSearchQuery(value);
              setDropdownOpen(true);
            }}
            placeholder={t('tourEdit.tourPlayerAddPlaceholder')}
            placeholderTextColor={appColors.text.muted}
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => {
              setTimeout(() => setDropdownOpen(false), 200);
            }}
          />
          {showDropdown && (
            <View style={styles.dropdown}>
              {filteredOptions.map((player, index) => (
                <Pressable
                  key={player.id}
                  style={[styles.dropdownItem, index === 0 && styles.dropdownItemFirst]}
                  onPress={() => handleSelectPlayer(player)}
                >
                  <Text style={styles.dropdownItemText}>{player.name}</Text>
                  {player.isFriend && (
                    <View style={styles.friendBadge}>
                      <Text style={styles.friendBadgeText}>{t('tourEdit.tourPlayerFriendBadge')}</Text>
                    </View>
                  )}
                </Pressable>
              ))}
              {showCreateTemporary && (
                <Pressable
                  style={[styles.dropdownItemCreate, filteredOptions.length === 0 && styles.dropdownItemFirst]}
                  onPress={handleCreateTemporary}
                >
                  <Text style={styles.createIcon}>+</Text>
                  <Text style={styles.dropdownItemCreateText}>
                    {t('tourEdit.tourPlayerAddAsNew', { name: searchQuery.trim() })}
                  </Text>
                </Pressable>
              )}
            </View>
          )}
        </View>
      )}

      {!canAdd && (
        <Text style={styles.maxText}>
          {existingRows.length > MAX_PLAYERS
            ? t('tourEdit.tourPlayersOverLimit', { count: existingRows.length, max: MAX_PLAYERS })
            : t('tourEdit.tourPlayersMaxReached', { max: MAX_PLAYERS })}
        </Text>
      )}

      <ConfirmDialog
        visible={confirmRemove !== null}
        title={t('tourEdit.tourPlayerRemoveTitle')}
        message={t('tourEdit.tourPlayerRemoveMessage', {
          name: confirmRemove?.name ?? t('tourEdit.tourPlayerRemoveFallbackName'),
        })}
        confirmLabel={t('tourEdit.removePlayer')}
        cancelLabel={t('tourEdit.confirmCancel')}
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...card,
    gap: 12,
  },
  playerList: {
    gap: 6,
  },
  playerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: appColors.surface.input,
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 10,
    paddingHorizontal: 14,
    minHeight: 48,
  },
  playerName: {
    fontSize: 15,
    fontWeight: '600',
    color: appColors.text.primary,
    flex: 1,
  },
  removeButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.4,
  },
  removeIcon: {
    fontSize: 20,
    fontWeight: '600',
    color: appColors.text.secondary,
    lineHeight: 22,
  },
  // The frame lives on the wrapper, not the input, because the results dropdown attaches
  // underneath as one connected unit. Same treatment as GoffyInput: an accent border stays
  // legible on the card behind it, where the old tonal border was invisible.
  searchWrapper: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    borderRadius: 10,
    backgroundColor: appColors.surface.input,
    overflow: 'hidden',
  },
  searchInput: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    fontWeight: '500',
    color: appColors.text.primary,
  },
  searchInputOpen: {
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
  },
  dropdown: {
    backgroundColor: appColors.surface.elevated,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: appColors.border.default,
  },
  dropdownItemFirst: {
    borderTopWidth: 0,
  },
  dropdownItemText: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
    flex: 1,
  },
  friendBadge: {
    backgroundColor: appColors.accent.subtleBg,
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  friendBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: appColors.accent.primary,
  },
  dropdownItemCreate: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: appColors.surface.alt,
  },
  createIcon: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.accent.primary,
  },
  dropdownItemCreateText: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.accent.positive,
    fontStyle: 'italic',
  },
  maxText: {
    fontSize: 13,
    color: appColors.text.muted,
    fontStyle: 'italic',
  },
});
